// Two-line effective-date cell for the variation table. Top row: the
// effective date in `D MMM YYYY` form. Bottom row: tone-aware combined
// status + proximity meta-line driven by `formatVariationDate`. Mirrors
// the SowDateCell shape so the two register surfaces read with the same
// vocabulary.

import {
  formatVariationDate,
  type VariationDateTone,
} from "./variationDateFormat";
import type { VariationStatus } from "../../../../types";

interface VariationDateCellProps {
  /** Optional — Proposed variations may not have a finalised date.
   *  When missing, the cell renders an em-dash and a status-only meta
   *  line via `formatVariationDate`. */
  effectiveDate?: string;
  status: VariationStatus;
  approvedAt?: string;
  rejectedAt?: string;
  /** Backend createdAt — we use this as the proposedAt anchor since
   *  the variation is created in the proposed state. */
  proposedAt?: string;
}

const TONE_CLASS: Record<VariationDateTone, string> = {
  muted: "text-slate-400 dark:text-slate-500",
  neutral: "text-slate-700 dark:text-slate-200",
  success: "text-emerald-600 dark:text-emerald-400",
  brand: "text-brand-600 dark:text-brand-400",
  danger: "text-red-600 dark:text-red-400 font-medium",
};

export default function VariationDateCell({
  effectiveDate,
  status,
  approvedAt,
  rejectedAt,
  proposedAt,
}: VariationDateCellProps) {
  const { effective, meta, tone } = formatVariationDate({
    effectiveDate,
    status,
    approvedAt,
    rejectedAt,
    proposedAt,
  });

  return (
    <div className="flex flex-col">
      <span className="text-body text-slate-900 dark:text-slate-100">
        {effective}
      </span>
      <span className={`text-caption ${TONE_CLASS[tone]}`}>{meta}</span>
    </div>
  );
}
